import React, { useMemo, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Monitor, Laptop, Smartphone, Tablet, AlertTriangle, Shield, Clock, CheckCircle, XCircle } from 'lucide-react';

const PlatformComparisonDashboard = ({ devices }) => {
  const [activeTab, setActiveTab] = useState('comparative');
  // Calculate platform distribution
  const platformData = useMemo(() => {
    const platformCounts = devices.reduce((acc, device) => {
      const platform = device.platform || 'plat-windows';
      const platformName = platform.replace('plat-', '').charAt(0).toUpperCase() + platform.replace('plat-', '').slice(1);
      acc[platformName] = (acc[platformName] || 0) + 1;
      return acc;
    }, {});

    const colors = {
      'Windows': '#0078D4',
      'Linux': '#FCC624',
      'Macos': '#A2AAAD',
      'Android': '#3DDC84',
      'Ios': '#000000'
    };

    return Object.entries(platformCounts).map(([name, value]) => ({
      name,
      value,
      color: colors[name] || '#6B7280'
    }));
  }, [devices]);

  // Calculate compliance by platform
  const complianceData = useMemo(() => {
    const platformCompliance = devices.reduce((acc, device) => {
      const platform = device.platform || 'plat-windows';
      const platformName = platform.replace('plat-', '').charAt(0).toUpperCase() + platform.replace('plat-', '').slice(1);
      
      if (!acc[platformName]) {
        acc[platformName] = { compliant: 0, nonCompliant: 0, total: 0 };
      }
      
      acc[platformName].total++;
      if (device.status === 'online' || device.compliance_status === 'compliant') {
        acc[platformName].compliant++;
      } else {
        acc[platformName].nonCompliant++;
      }
      
      return acc;
    }, {});

    return Object.entries(platformCompliance).map(([name, data]) => ({
      name,
      Compliant: data.compliant,
      'Non-Compliant': data.nonCompliant,
      total: data.total
    }));
  }, [devices]);

  // Calculate alerts by platform
  const alertsData = useMemo(() => {
    const platformAlerts = devices.reduce((acc, device) => {
      const platform = device.platform || 'plat-windows';
      const platformName = platform.replace('plat-', '').charAt(0).toUpperCase() + platform.replace('plat-', '').slice(1);
      
      if (!acc[platformName]) {
        acc[platformName] = { critical: 0, warning: 0, total: 0 };
      }
      
      acc[platformName].total++;
      if (device.status === 'critical' || device.status === 'offline') {
        acc[platformName].critical++;
      } else if (device.status === 'warning') {
        acc[platformName].warning++;
      }
      
      return acc;
    }, {});

    return Object.entries(platformAlerts).map(([name, data]) => ({
      name,
      Critical: data.critical,
      Warning: data.warning,
      Healthy: data.total - data.critical - data.warning
    }));
  }, [devices]);

  // Calculate OS version fragmentation
  const osVersionData = useMemo(() => {
    const versionCounts = devices.reduce((acc, device) => {
      const os = device.os || 'Unknown';
      const platform = device.platform || 'plat-windows';
      const platformName = platform.replace('plat-', '').charAt(0).toUpperCase() + platform.replace('plat-', '').slice(1);
      
      const key = `${platformName} - ${os}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(versionCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10 versions
  }, [devices]);

  // Calculate EOL devices (simplified - would need actual EOL data)
  const eolDevices = useMemo(() => {
    const eolPatterns = [
      /Windows 7/i,
      /Windows 8/i,
      /Android [1-9]\./i,
      /iOS 1[0-3]\./i
    ];
    
    return devices.filter(device => {
      const os = device.os || '';
      return eolPatterns.some(pattern => pattern.test(os));
    });
  }, [devices]);

  // Calculate patch status (mock data - would come from backend)
  const patchStatusData = useMemo(() => {
    const platformPatch = devices.reduce((acc, device) => {
      const platform = device.platform || 'plat-windows';
      const platformName = platform.replace('plat-', '').charAt(0).toUpperCase() + platform.replace('plat-', '').slice(1);
      
      if (!acc[platformName]) {
        acc[platformName] = { days: 0, count: 0 };
      }
      
      // Mock patch latency (would come from backend)
      const mockLatency = platformName === 'Windows' ? 3 : platformName === 'Linux' ? 15 : platformName === 'Macos' ? 5 : 7;
      acc[platformName].days += mockLatency;
      acc[platformName].count++;
      
      return acc;
    }, {});

    return Object.entries(platformPatch).map(([name, data]) => ({
      name,
      'Avg Days': Math.round(data.days / data.count) || 0
    }));
  }, [devices]);

  const totalDevices = devices.length;
  const platformDistributionPercent = platformData.reduce((acc, item) => {
    acc[item.name] = totalDevices > 0 ? Math.round((item.value / totalDevices) * 100) : 0;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-1 flex gap-2">
        <button 
          onClick={() => setActiveTab('comparative')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'comparative' 
              ? 'bg-blue-600 text-white' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Comparative View
        </button>
        <button 
          onClick={() => setActiveTab('lifecycle')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'lifecycle' 
              ? 'bg-blue-600 text-white' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Lifecycle & Version
        </button>
      </div>

      {/* Comparative View */}
      {activeTab === 'comparative' && (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Total Devices</p>
                <p className="text-2xl font-bold text-slate-800">{totalDevices}</p>
              </div>
              <Monitor className="text-blue-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Platforms</p>
                <p className="text-2xl font-bold text-slate-800">{platformData.length}</p>
              </div>
              <Laptop className="text-green-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">EOL Devices</p>
                <p className="text-2xl font-bold text-red-600">{eolDevices.length}</p>
              </div>
              <AlertTriangle className="text-red-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Avg Patch Days</p>
                <p className="text-2xl font-bold text-slate-800">
                  {patchStatusData.length > 0 
                    ? Math.round(patchStatusData.reduce((sum, p) => sum + p['Avg Days'], 0) / patchStatusData.length)
                    : 0}
                </p>
              </div>
              <Clock className="text-orange-500" size={32} />
            </div>
          </div>
        </div>

        {/* Platform Distribution Donut Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Monitor size={20} className="text-blue-500" />
            Platform Distribution
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={platformData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {platformData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col justify-center space-y-3">
              {platformData.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
                    <span className="text-sm font-medium text-slate-700">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-slate-800">{item.value}</span>
                    <span className="text-xs text-slate-500 ml-2">({platformDistributionPercent[item.name]}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Compliance by Platform */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Shield size={20} className="text-green-500" />
            Compliance by Platform
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={complianceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Compliant" stackId="a" fill="#10B981" />
              <Bar dataKey="Non-Compliant" stackId="a" fill="#EF4444" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
            {complianceData.map((item, index) => {
              const complianceRate = item.total > 0 ? Math.round((item.Compliant / item.total) * 100) : 0;
              return (
                <div key={index} className="text-center p-3 bg-slate-50 rounded">
                  <p className="text-xs text-slate-500 mb-1">{item.name}</p>
                  <p className={`text-lg font-bold ${complianceRate >= 80 ? 'text-green-600' : complianceRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {complianceRate}%
                  </p>
                  <p className="text-xs text-slate-400">{item.Compliant}/{item.total}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Alerts by Platform */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-500" />
            Alerts by Platform
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={alertsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Critical" stackId="a" fill="#DC2626" />
              <Bar dataKey="Warning" stackId="a" fill="#F59E0B" />
              <Bar dataKey="Healthy" stackId="a" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}

      {/* Lifecycle & Version View */}
      {activeTab === 'lifecycle' && (
      <div className="space-y-6">
        {/* OS Version Fragmentation */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4">OS Version Fragmentation</h3>
          <div className="space-y-2">
            {osVersionData.map((item, index) => {
              const percentage = totalDevices > 0 ? Math.round((item.count / totalDevices) * 100) : 0;
              return (
                <div key={index} className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">{item.name}</span>
                      <span className="text-sm text-slate-500">{item.count} devices ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* EOL Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6 bg-red-50/30">
          <h3 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-600" />
            End-of-Life (EOL) Devices
          </h3>
          {eolDevices.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-red-700 mb-4">
                <strong>{eolDevices.length}</strong> devices are running unsupported OS versions that may have security vulnerabilities.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {eolDevices.slice(0, 10).map((device, index) => (
                  <div key={index} className="p-3 bg-white border border-red-200 rounded flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{device.name || device.id}</p>
                      <p className="text-xs text-slate-500">{device.os}</p>
                    </div>
                    <XCircle className="text-red-500" size={20} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle size={20} />
              <p className="text-sm">All devices are running supported OS versions.</p>
            </div>
          )}
        </div>

        {/* Patch Status */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Clock size={20} className="text-orange-500" />
            Average Patch Latency by Platform
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={patchStatusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="Avg Days" fill="#F59E0B" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded">
            <p className="text-xs text-orange-700">
              <strong>Note:</strong> Patch latency represents the average number of days between patch release and deployment across devices in each platform.
            </p>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default PlatformComparisonDashboard;

